<script setup>
import {ref, watch, defineProps, defineEmits, onMounted, computed} from "vue";
import axios from "axios";
import {useCustomerStore} from "@/stores/Customer";

const props = defineProps({
    value: [Number, Array],
    readonly: Boolean,
    disabled: Boolean,
    label: String,
    rules: Array,
    multiple: Boolean,
});
const emit = defineEmits(["input"]);

const loading = ref(false);
const dialog = ref(false);
const customerStore = useCustomerStore();
const customer = ref({addresses: []});
const search = ref("");
const type = ref("physician");

const itemsWithSelected = computed(() => {
    const storeCustomers = customerStore.selectCustomers || []

    // If no customer is currently selected, just return the store's list
    if (!customer.value.id) {
        return storeCustomers
    }

    // Filter the store's list to remove the selected customer if they are already in it
    // (Prevents the selected customer from showing up twice)
    const filteredCustomers = storeCustomers.filter(
        (c) => c.id !== customer.value.id
    )

    // Return a new array with the selected customer at index 0
    return [customer.value, ...filteredCustomers]

})

const headers = [
    {
        text: type.value === 'wholesaler' ? 'Dénomination' : 'Nom / Prénom',
        align: "start",
        sortable: true,
        value: "lastName",
    },
    {
        text: "Potentiel",
        align: "start",
        sortable: true,
        value: "potentialName",
    },
    {
        text: "Téléphone",
        align: "start",
        sortable: true,
        value: "phone",
    },
    {
        text: "Adresse",
        align: "start",
        sortable: true,
        value: "address",
    },
];

function findById(id) {
    axios.get(`/customers/${id}`).then(response => {
        customer.value = response.data;
    });
}

function findAll() {
    loading.value = true;
    axios.get(`/customers`, {
        params: {
            search: search.value,
            type: type.value,
            rowsPerPage: 10,
            page: 0,
        }
    }).then(response => {
        customerStore.setSelectCustomers(response.data.elements);
    }).finally(() => {
        loading.value = false;
    });
}

watch(() => props.value, (newValue) => {
    if (newValue) {
        findById(newValue);
    } else {
        customer.value = {addresses: []};
    }
}, {immediate: true});

watch(() => [search.value, type.value], () => {
    findAll();
})

function onClick() {
    dialog.value = true;
}

function onClear() {
    alert("abd")
    customer.value = {addresses: []};
    emit('input', null);
    // findAll();
}

function rowDbClick(item) {
    if (!props.readonly) {
        customer.value = item;
        emit('input', item.id);
        dialog.value = false;
    }
}

watch(search, (val) => {
    if (!val) {
        findAll();
    }
});

onMounted(() => {
    findAll();
})
</script>

<template>
    <div>
        <v-dialog
            v-model="dialog"
            persistent
            max-width="800"
            scrollable
        >
            <v-card>
                <w-toolbar color="teal" :title="$t('module.customer')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-label :label="$t('common.search')"/>
                                <w-text-field
                                    v-model="search"
                                    :readonly="readonly"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-radio-group
                                    v-model="type"
                                    row
                                    :readonly="readonly"
                                >
                                    <v-radio value="physician" :label="$t('module.physician')"/>
                                    <v-radio value="pharmacy" :label="$t('module.pharmacy')"/>
                                    <v-radio value="wholesaler" :label="$t('module.wholesaler')"/>
                                </v-radio-group>
                            </v-col>
                        </v-row>
                        <w-data-table
                            :headers="headers"
                            :items="itemsWithSelected"
                            :loading="loading"
                            disable-pagination
                            @rowDbClick="rowDbClick"
                        >

                            <template v-slot:[`item.lastName`]="{ item }">
                                <w-inline-text
                                    bold
                                    :text="item.name"
                                    :icon="item.id === value ? 'mdi-check-circle': null"
                                    iconColor="teal"
                                />
                            </template>
                            <template v-slot:[`item.potentialName`]="{ item }">
                                <w-inline-text :text="item.potentialName"/>
                            </template>
                            <template v-slot:[`item.phone`]="{ item }">
                                {{ item.addresses.find(e => e.type === 'PRINCIPAL')?.fixPhone }} /
                                {{ item.addresses.find(e => e.type === 'PRINCIPAL')?.mobilePhone }}
                            </template>
                            <template v-slot:[`item.address`]="{ item }">
                                {{ item.addresses.find(e => e.type === 'PRINCIPAL')?.location }}
                            </template>
                        </w-data-table>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="dialog = false"/>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <w-text-field
            :value="customer.name"
            :label="label"
            :readonly="readonly"
            :disabled="disabled"
            @press="onClick"
            @onClear="onClear"
            :rules="rules"
            hide-details="auto"
        />

        <input type="hidden">
    </div>
</template>